import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Shield } from 'lucide-react';
import api from '../../../../api/client';
import PageHeader, { PageCard, PageShell } from '../../../common/PageHeader';
import NotificationPopup from '../../component/NotificationPopup';
import AptiviewAppStatus from '../companyadmins/AptiviewAppStatus';
import { usePageLoading } from '../../../../contexts/LoadingContext';
import InterviewConfigurationPanel from './InterviewConfigurationPanel';
import QuestionnaireSettings from './QuestionnaireSettings';

const TAB_CONFIGURATION = 'configuration';
const TAB_QUESTIONNAIRE = 'questionnaire';
const FONT = { fontFamily: "'Poppins', sans-serif" };
const GRADIENT = 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)';

export default function InterviewConfigurationPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [questionnaireVisited, setQuestionnaireVisited] = useState(false);

  const activeTab = useMemo(() => {
    const tab = searchParams.get('tab');
    return tab === TAB_QUESTIONNAIRE ? TAB_QUESTIONNAIRE : TAB_CONFIGURATION;
  }, [searchParams]);

  usePageLoading(
    activeTab === TAB_CONFIGURATION && (loading || (companies.length > 0 && !selectedCompany)),
    { message: t('common.loading', 'Loading…') }
  );

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setUserDetails(user);

    const isSuperAdmin = user?.is_superuser === true;
    const isCompanyAdmin = Array.isArray(user?.admin_companies) && user.admin_companies.length > 0;

    if (!isSuperAdmin && !isCompanyAdmin) {
      setError(t('companyAdmins.accessDeniedMessage'));
      setLoading(false);
      return;
    }

    const fetchCompanies = async () => {
      try {
        const response = await api.get('/companies/');
        setCompanies(response.data.results || response.data || []);
      } catch (err) {
        console.error('Error fetching companies:', err);
        setError(t('companyAdmins.failedToLoadCompanies'));
        setSnackbar({
          open: true,
          message: t('companyAdmins.failedToLoadCompanies'),
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCompanies();
  }, [t]);

  useEffect(() => {
    if (!companies.length || !userDetails) return;
    const visibleCompanies = userDetails.is_superuser
      ? companies
      : companies.filter((company) => (
        company.name !== 'Practice Company'
        && Array.isArray(userDetails.admin_companies)
        && userDetails.admin_companies.includes(company.name)
      ));
    if (visibleCompanies.length === 0) return;
    setSelectedCompany((current) => {
      if (current && visibleCompanies.some((company) => company.id === current)) {
        return current;
      }
      return visibleCompanies[0].id;
    });
  }, [companies, userDetails]);

  useEffect(() => {
    if (activeTab === TAB_QUESTIONNAIRE) {
      setQuestionnaireVisited(true);
    }
  }, [activeTab]);

  const handleTabChange = (nextTab) => {
    if (nextTab === TAB_CONFIGURATION && window.hasUnsavedQuestionnaireChanges) {
      if (!window.confirm('You have unsaved questionnaire changes. Are you sure you want to leave?')) {
        return;
      }
      window.hasUnsavedQuestionnaireChanges = false;
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', nextTab);
    setSearchParams(nextParams, { replace: true });
  };

  const handleVerificationRequiredChange = async (companyId, value) => {
    try {
      const { data } = await api.patch(`/companies/${companyId}/`, { verification_required: value });
      setCompanies((prev) =>
        prev.map((c) => {
          if (c.id !== Number(companyId)) return c;
          const updated = { ...c, verification_required: data.verification_required, showing_identity: data.showing_identity };
          if (updated.verification_required === false) updated.showing_identity = false;
          return updated;
        })
      );
      setSnackbar({
        open: true,
        message: t('companyAdmins.verificationRequiredUpdated'),
        severity: 'success',
      });
    } catch (err) {
      console.error('Error updating verification_required:', err);
      const message =
        err.response?.status === 403
          ? t('companyAdmins.verificationRequiredForbidden')
          : err.response?.data?.error || err.response?.data?.detail || t('companyAdmins.verificationRequiredUpdateFailed');
      setSnackbar({
        open: true,
        message,
        severity: 'error',
      });
    }
  };

  const handleShowingIdentityChange = async (companyId, value) => {
    try {
      const { data } = await api.patch(`/companies/${companyId}/`, { showing_identity: value });
      setCompanies((prev) =>
        prev.map((c) => (c.id === Number(companyId) ? { ...c, showing_identity: data.showing_identity } : c))
      );
      setSnackbar({
        open: true,
        message: t('companyAdmins.showingIdentityUpdated'),
        severity: 'success',
      });
    } catch (err) {
      console.error('Error updating showing_identity:', err);
      const message =
        err.response?.data?.showing_identity?.[0] ||
        err.response?.data?.error ||
        err.response?.data?.detail ||
        t('companyAdmins.showingIdentityUpdateFailed');
      setSnackbar({
        open: true,
        message,
        severity: 'error',
      });
    }
  };

  const tabButtonClass = (isActive) =>
    `px-3.5 py-1.5 rounded-lg text-sm font-semibold transition-all duration-200 cursor-pointer ${
      isActive
        ? 'text-white shadow-sm'
        : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
    }`;

  if (error && !loading) {
    return (
      <PageShell fill={false}>
        <div className="p-6">
          <div className="max-w-2xl mx-auto">
            <div className="bg-red-50 border border-red-200 rounded-xl p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Shield className="h-6 w-6 text-red-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-red-800">{t('companyAdmins.accessDenied')}</h3>
                  <p className="text-red-700 mt-1">{error}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell className="bg-[#f8fafc]" style={FONT}>
      <NotificationPopup
        open={snackbar.open}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        severity={snackbar.severity}
        message={snackbar.message}
      />
      <PageCard overflow={false}>
        <PageHeader
          title={t('companyAdmins.interviewConfig.title', 'Interview Configuration')}
          subtitle={t(
            'companyAdmins.interviewConfig.combinedDescription',
            'Set interview defaults, identity checks, and monitor Interview App health.'
          )}
        />
        <div className="flex gap-2 px-4 pt-2.5 pb-2 shrink-0 border-b border-gray-100">
          <button
            type="button"
            onClick={() => handleTabChange(TAB_CONFIGURATION)}
            style={{
              fontFamily: FONT.fontFamily,
              ...(activeTab === TAB_CONFIGURATION ? { background: GRADIENT } : {}),
            }}
            className={tabButtonClass(activeTab === TAB_CONFIGURATION)}
          >
            {t('companyAdmins.interviewConfig.configurationTab', 'Configuration')}
          </button>
          <button
            type="button"
            onClick={() => handleTabChange(TAB_QUESTIONNAIRE)}
            style={{
              fontFamily: FONT.fontFamily,
              ...(activeTab === TAB_QUESTIONNAIRE ? { background: GRADIENT } : {}),
            }}
            className={tabButtonClass(activeTab === TAB_QUESTIONNAIRE)}
          >
            {t('companyAdmins.interviewConfig.questionnaireTab', 'Questionnaire')}
          </button>
        </div>
        <div
          className="flex-1 min-h-0 overflow-y-auto"
          style={{ display: activeTab === TAB_CONFIGURATION ? 'block' : 'none' }}
        >
          <InterviewConfigurationPanel
            selectedCompany={selectedCompany}
            companies={companies}
            setCompanies={setCompanies}
            loading={loading}
            onNotify={setSnackbar}
            onVerificationRequiredChange={handleVerificationRequiredChange}
            onShowingIdentityChange={handleShowingIdentityChange}
          />
          <div className="px-4 pb-5 sm:px-5">
            <AptiviewAppStatus compact />
          </div>
        </div>
        <div
          className="flex-1 min-h-0 overflow-y-auto"
          style={{ display: activeTab === TAB_QUESTIONNAIRE ? 'block' : 'none' }}
        >
          {(questionnaireVisited || activeTab === TAB_QUESTIONNAIRE) && (
            <QuestionnaireSettings embedded />
          )}
        </div>
      </PageCard>
    </PageShell>
  );
}
